import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { EDITORIAL_STATUSES, statusBadge } from './editorial';
import BlockEditor from '../../components/BlockEditor';

const BLOCKS_TEMPLATE = JSON.stringify([
  { type: 'heading', level: 2, text: 'Titolo della sezione' },
  { type: 'paragraph', text: 'Testo del paragrafo...' },
  { type: 'box_remember', text: 'Concetto chiave da ricordare.' },
  { type: 'box_warning', text: 'Errore frequente nei quiz.' },
  { type: 'table', headers: ['Colonna 1', 'Colonna 2'], rows: [['a', 'b']] },
  { type: 'concept_map', data: { title: 'Mappa', root: { label: 'Nodo radice', children: [{ label: 'Figlio' }] } } },
], null, 2);

const CONFIGS = {
  subjects: {
    label: 'Materie', idField: 'subject_id',
    columns: [['name', 'Nome'], ['description', 'Descrizione']],
    fields: [
      { name: 'name', label: 'Nome' },
      { name: 'description', label: 'Descrizione', type: 'textarea' },
      { name: 'status', label: 'Stato', type: 'status' },
    ],
    empty: { name: '', description: '', status: 'DRAFT', country: 'IT', language: 'it' },
  },
  modules: {
    label: 'Moduli', idField: 'module_id',
    columns: [['title', 'Titolo'], ['subject_id', 'Materia']],
    fields: [
      { name: 'subject_id', label: 'Materia', type: 'ref', ref: 'subjects', refLabel: 'name' },
      { name: 'title', label: 'Titolo' },
      { name: 'description', label: 'Descrizione', type: 'textarea' },
      { name: 'order', label: 'Ordine', type: 'number' },
      { name: 'status', label: 'Stato', type: 'status' },
    ],
    empty: { subject_id: '', title: '', description: '', order: 0, status: 'DRAFT', country: 'IT', language: 'it' },
  },
  chapters: {
    label: 'Capitoli', idField: 'chapter_id',
    columns: [['title', 'Titolo'], ['module_id', 'Modulo'], ['order', 'Ordine']],
    fields: [
      { name: 'module_id', label: 'Modulo', type: 'ref', ref: 'modules', refLabel: 'title' },
      { name: 'title', label: 'Titolo' },
      { name: 'order', label: 'Ordine', type: 'number' },
      { name: 'status', label: 'Stato', type: 'status' },
    ],
    empty: { module_id: '', title: '', order: 0, status: 'DRAFT' },
  },
  sections: {
    label: 'Sezioni', idField: 'section_id',
    columns: [['title', 'Titolo'], ['chapter_id', 'Capitolo'], ['pages_equiv', 'Pagine']],
    fields: [
      { name: 'chapter_id', label: 'Capitolo', type: 'ref', ref: 'chapters', refLabel: 'title' },
      { name: 'title', label: 'Titolo' },
      { name: 'order', label: 'Ordine', type: 'number' },
      { name: 'pages_equiv', label: 'Pagine equivalenti', type: 'number' },
      { name: 'learning_priority', label: 'Priorità di studio', type: 'priority' },
      { name: 'status', label: 'Stato', type: 'status' },
      { name: 'blocks', label: 'Blocchi di contenuto', type: 'json' },
    ],
    empty: { chapter_id: '', title: '', order: 0, pages_equiv: 1, learning_priority: 'SHOULD_KNOW', status: 'DRAFT', blocks: [] },
  },
};

function CrudTab({ name }) {
  const cfg = CONFIGS[name];
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(cfg.empty);

  const load = () => {
    api.get(`/admin/${name}`).then(({ data }) => setItems(data.items)).catch(() => {});
    cfg.fields.filter((f) => f.type === 'ref').forEach((f) => {
      api.get(`/admin/${f.ref}`).then(({ data }) => setRefs((r) => ({ ...r, [f.ref]: data.items }))).catch(() => {});
    });
  };
  // `load` depends on the active dynamic CRUD configuration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [name]);

  const refName = (ref, id) => {
    const f = cfg.fields.find((x) => x.ref === ref);
    const item = (refs[ref] || []).find((i) => i[CONFIGS[ref].idField] === id);
    return item ? item[f.refLabel] : id;
  };

  const openEdit = (item) => {
    setEditing(item[cfg.idField]);
    setForm({ ...cfg.empty, ...item });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form };
    cfg.fields.filter((f) => f.type === 'number').forEach((f) => { payload[f.name] = Number(payload[f.name]) || 0; });
    try {
      if (editing) await api.put(`/admin/${name}/${editing}`, payload);
      else await api.post(`/admin/${name}`, payload);
      toast.success('Salvato');
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare definitivamente?')) return;
    await api.delete(`/admin/${name}/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{items.length} elementi</p>
        <Button onClick={() => { setEditing(null); const e = { ...cfg.empty }; const jf = cfg.fields.find((f) => f.type === 'json'); if (jf) e[jf.name] = JSON.parse(BLOCKS_TEMPLATE); setForm(e); setOpen(true); }}
          data-testid={`${name}-create`} size="sm" className="rounded-full bg-brand hover:bg-brand-hover text-white">
          <Plus size={14} strokeWidth={1.5} className="mr-1" /> Nuovo
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {cfg.columns.map(([, l]) => <th key={l} className="px-5 py-3 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{l}</th>)}
              <th className="px-5 py-3 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">Stato</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => (
              <tr key={item[cfg.idField]} data-testid={`${name}-row-${item[cfg.idField]}`}>
                {cfg.columns.map(([k]) => (
                  <td key={k} className="px-5 py-3 text-slate-700 max-w-[220px] truncate">
                    {k.endsWith('_id') && cfg.fields.find((f) => f.name === k)?.ref ? refName(cfg.fields.find((f) => f.name === k).ref, item[k]) : String(item[k] ?? '')}
                  </td>
                ))}
                <td className="px-5 py-3">{statusBadge(item.status)}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(item)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`${name}-edit-${item[cfg.idField]}`}><Pencil size={14} strokeWidth={1.5} /></button>
                    <button onClick={() => remove(item[cfg.idField])} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`${name}-delete-${item[cfg.idField]}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Modifica' : 'Crea'} — {cfg.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {cfg.fields.map((f) => (
              <div key={f.name}>
                <Label>{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea className="mt-1.5" rows={3} value={form[f.name] ?? ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                ) : f.type === 'json' ? (
                  <BlockEditor value={form[f.name] || []} onChange={(blocks) => setForm({ ...form, [f.name]: blocks })} />
                ) : f.type === 'priority' ? (
                  <Select value={form[f.name]} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{[['MUST_KNOW','Devi saperlo'],['SHOULD_KNOW','Da conoscere'],['DEEP_DIVE','Approfondimento']].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === 'status' ? (
                  <Select value={form[f.name]} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{EDITORIAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === 'ref' ? (
                  <Select value={form[f.name]} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger className="mt-1.5" data-testid={`${name}-ref-${f.name}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {(refs[f.ref] || []).map((r) => <SelectItem key={r[CONFIGS[f.ref].idField]} value={r[CONFIGS[f.ref].idField]}>{r[f.refLabel]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input className="mt-1.5" type={f.type === 'number' ? 'number' : 'text'} data-testid={`${name}-field-${f.name}`}
                    value={form[f.name] ?? ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                )}
              </div>
            ))}
            <Button onClick={save} data-testid={`${name}-save`} className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminContent() {
  return (
    <div data-testid="admin-content">
      <h2 className="font-display font-semibold text-lg text-slate-900 mb-1">Struttura dei contenuti</h2>
      <p className="text-sm text-slate-500 mb-6">Materia → Modulo → Capitolo → Sezione. I moduli sono riutilizzabili tra più concorsi: una modifica qui si propaga ovunque.</p>
      <Tabs defaultValue="subjects">
        <TabsList className="mb-6">
          {Object.entries(CONFIGS).map(([k, c]) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{c.label}</TabsTrigger>)}
        </TabsList>
        {Object.keys(CONFIGS).map((k) => (
          <TabsContent key={k} value={k}><CrudTab name={k} /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
